import {
  cloneElement,
  type CSSProperties,
  type KeyboardEvent,
  type ReactElement,
} from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Icon, cn } from "@nexus/ui";

type OptionProps = {
  className?: string;
  style?: CSSProperties;
  children?: React.ReactNode;
};

interface SortablePresetRowProps {
  id: string;
  disabled: boolean;
  reorderAria: string;
  onMove: (id: string, delta: -1 | 1) => void;
  children: ReactElement;
}

export function SortablePresetRow({
  id,
  disabled,
  reorderAria,
  onMove,
  children,
}: SortablePresetRowProps) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({
      id,
      disabled,
    });

  const grip = (
    <button
      type="button"
      ref={setActivatorNodeRef}
      className={cn(
        "flex min-h-11 min-w-11 shrink-0 touch-none items-center justify-center rounded text-muted-foreground/50 [@media(hover:hover)_and_(pointer:fine)]:min-h-6 [@media(hover:hover)_and_(pointer:fine)]:min-w-6",
        disabled
          ? "cursor-not-allowed"
          : "cursor-grab hover:text-muted-foreground active:cursor-grabbing",
      )}
      aria-label={reorderAria}
      disabled={disabled}
      {...attributes}
      {...listeners}
      onPointerDown={(event) => {
        event.stopPropagation();
        listeners?.onPointerDown?.(event);
      }}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event: KeyboardEvent<HTMLButtonElement>) => {
        if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
        event.preventDefault();
        event.stopPropagation();
        onMove(id, event.key === "ArrowUp" ? -1 : 1);
      }}
    >
      <Icon name="grip-vertical" size="sm" color="inherit" aria-hidden />
    </button>
  );

  const childProps = children.props as OptionProps;
  return cloneElement(children as ReactElement<OptionProps>, {
    ref: setNodeRef,
    style: {
      ...childProps.style,
      transform: CSS.Transform.toString(transform),
      transition,
    },
    className: cn(childProps.className, isDragging && "opacity-80"),
    children: (
      <>
        {grip}
        {childProps.children}
      </>
    ),
  } as OptionProps & { ref: typeof setNodeRef });
}
